import React from 'react';
import { Link } from 'react-router-dom';

type LinkProps = {
  to: string;
  className?: string;
  children: React.ReactNode;
};

function DocLink({ to, className, children }: LinkProps) {
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}

type Props = {
  t: (key: string) => string;
  sectionClass?: string;
  introClass?: string;
  linkClass?: string;
  LinkComponent?: React.ComponentType<LinkProps>;
};

export function TermsDocumentContent({
  t,
  sectionClass = 'legal-page-section',
  introClass = 'legal-page-intro',
  linkClass = 'legal-page-inline-link',
  LinkComponent = DocLink,
}: Props) {
  const L = LinkComponent;
  return (
    <>
      <p className={introClass}>{t('termsOfService.intro')}</p>
      <section className={sectionClass}>
        <h2>{t('termsOfService.s1.title')}</h2>
        <p>{t('termsOfService.s1.p')}</p>
      </section>
      <section className={sectionClass}>
        <h2>{t('termsOfService.s2.title')}</h2>
        <ul>
          <li>{t('termsOfService.s2.li1')}</li>
          <li>{t('termsOfService.s2.li2')}</li>
          <li>{t('termsOfService.s2.li3')}</li>
        </ul>
      </section>
      <section className={sectionClass}>
        <h2>{t('termsOfService.s3.title')}</h2>
        <p>{t('termsOfService.s3.p1')}</p>
        <ul>
          <li>{t('termsOfService.s3.li1')}</li>
          <li>{t('termsOfService.s3.li2')}</li>
          <li>{t('termsOfService.s3.li3')}</li>
          <li>{t('termsOfService.s3.li4')}</li>
          <li>{t('termsOfService.s3.li5')}</li>
        </ul>
        <p>{t('termsOfService.s3.p2')}</p>
      </section>
      <section className={sectionClass}>
        <h2>{t('termsOfService.s4.title')}</h2>
        <p>
          {t('termsOfService.s4.pBefore')}
          <L to="/privacy" className={linkClass}>{t('termsOfService.s4.linkPrivacy')}</L>
          {t('termsOfService.s4.pSuffix')}
        </p>
      </section>
      <section className={sectionClass}>
        <h2>{t('termsOfService.s5.title')}</h2>
        <p>{t('termsOfService.s5.p')}</p>
      </section>
      <section className={sectionClass}>
        <h2>{t('termsOfService.s6.title')}</h2>
        <p>{t('termsOfService.s6.p')}</p>
      </section>
      <section className={sectionClass}>
        <h2>{t('termsOfService.s7.title')}</h2>
        <p>{t('termsOfService.s7.p')}</p>
      </section>
      <section className={sectionClass}>
        <h2>{t('termsOfService.s8.title')}</h2>
        <p>{t('termsOfService.s8.p')}</p>
      </section>
      <section className={sectionClass}>
        <h2>{t('termsOfService.s9.title')}</h2>
        <p>{t('termsOfService.s9.p')}</p>
      </section>
      <section className={sectionClass}>
        <h2>{t('termsOfService.s10.title')}</h2>
        <p>
          {t('termsOfService.s10.pBefore')}
          <L to="/privacy" className={linkClass}>{t('termsOfService.s10.linkPrivacy')}</L>
          {t('termsOfService.s10.pAnd')}
          <L to="/cookies-policy" className={linkClass}>{t('termsOfService.s10.linkCookies')}</L>
          {t('termsOfService.s10.pSuffix')}
        </p>
      </section>
      <section className={sectionClass}>
        <h2>{t('termsOfService.s11.title')}</h2>
        <ul>
          <li>{t('termsOfService.s11.li1')}</li>
          <li>{t('termsOfService.s11.li2')}</li>
          <li>{t('termsOfService.s11.li3')}</li>
        </ul>
        <p>{t('termsOfService.s11.p')}</p>
      </section>
    </>
  );
}

export function PrivacyDocumentContent({
  t,
  sectionClass = 'legal-page-section',
  introClass = 'legal-page-intro',
  linkClass = 'legal-page-inline-link',
  LinkComponent = DocLink,
}: Props) {
  const L = LinkComponent;
  return (
    <>
      <p className={introClass}>{t('privacyPolicy.intro')}</p>
      <section className={sectionClass}>
        <h2>{t('privacyPolicy.s1.title')}</h2>
        <p>{t('privacyPolicy.s1.p')}</p>
      </section>
      <section className={sectionClass}>
        <h2>{t('privacyPolicy.s2.title')}</h2>
        <p>{t('privacyPolicy.s2.p')}</p>
        <ul>
          <li><strong>{t('privacyPolicy.s2.li1Label')}</strong> {t('privacyPolicy.s2.li1Text')}</li>
          <li><strong>{t('privacyPolicy.s2.li2Label')}</strong> {t('privacyPolicy.s2.li2Text')}</li>
          <li><strong>{t('privacyPolicy.s2.li3Label')}</strong> {t('privacyPolicy.s2.li3Text')}</li>
          <li><strong>{t('privacyPolicy.s2.li4Label')}</strong> {t('privacyPolicy.s2.li4Prefix')}<L to="/cookies-policy" className={linkClass}>{t('privacyPolicy.s2.li4Link')}</L>{t('privacyPolicy.s2.li4Suffix')}</li>
          <li><strong>{t('privacyPolicy.s2.li5Label')}</strong> {t('privacyPolicy.s2.li5Text')}</li>
        </ul>
      </section>
      <section className={sectionClass}>
        <h2>{t('privacyPolicy.s3.title')}</h2>
        <p>{t('privacyPolicy.s3.p1')}</p>
        <ul>
          <li>{t('privacyPolicy.s3.li1')}</li>
          <li>{t('privacyPolicy.s3.li2')}</li>
          <li>{t('privacyPolicy.s3.li3')}</li>
          <li>{t('privacyPolicy.s3.li4')}</li>
          <li>{t('privacyPolicy.s3.li5')}</li>
          <li>{t('privacyPolicy.s3.li6')}</li>
        </ul>
        <p><strong>{t('privacyPolicy.s3.p2Label')}</strong> {t('privacyPolicy.s3.p2Text')}</p>
      </section>
      <section className={sectionClass}>
        <h2>{t('privacyPolicy.s4.title')}</h2>
        <p>{t('privacyPolicy.s4.p')}</p>
      </section>
      <section className={sectionClass}>
        <h2>{t('privacyPolicy.s5.title')}</h2>
        <p>{t('privacyPolicy.s5.p')}</p>
      </section>
      <section className={sectionClass}>
        <h2>{t('privacyPolicy.s6.title')}</h2>
        <p>{t('privacyPolicy.s6.p1')}</p>
        <ul>
          <li><strong>{t('privacyPolicy.s6.li1Label')}</strong> {t('privacyPolicy.s6.li1Text')}</li>
          <li><strong>{t('privacyPolicy.s6.li2Label')}</strong> {t('privacyPolicy.s6.li2Text')}</li>
          <li><strong>{t('privacyPolicy.s6.li3Label')}</strong> {t('privacyPolicy.s6.li3Text')}</li>
          <li><strong>{t('privacyPolicy.s6.li4Label')}</strong> {t('privacyPolicy.s6.li4Text')}</li>
          <li><strong>{t('privacyPolicy.s6.li5Label')}</strong> {t('privacyPolicy.s6.li5Text')}</li>
          <li><strong>{t('privacyPolicy.s6.li6Label')}</strong> {t('privacyPolicy.s6.li6Text')}</li>
        </ul>
        <p>{t('privacyPolicy.s6.p2')}</p>
      </section>
      <section className={sectionClass}>
        <h2>{t('privacyPolicy.s7.title')}</h2>
        <p>{t('privacyPolicy.s7.p')}</p>
      </section>
      <section className={sectionClass}>
        <h2>{t('privacyPolicy.s8.title')}</h2>
        <p>{t('privacyPolicy.s8.p')}</p>
      </section>
      <section className={sectionClass}>
        <h2>{t('privacyPolicy.s9.title')}</h2>
        <p>
          {t('privacyPolicy.s9.pBefore')}
          <L to="/terms" className={linkClass}>{t('privacyPolicy.s9.linkTerms')}</L>
          {t('privacyPolicy.s9.pAnd')}
          <L to="/cookies-policy" className={linkClass}>{t('privacyPolicy.s9.linkCookies')}</L>
          {t('privacyPolicy.s9.pSuffix')}
        </p>
      </section>
      <section className={sectionClass}>
        <h2>{t('privacyPolicy.s10.title')}</h2>
        <p>{t('privacyPolicy.s10.p1')}</p>
        <ul>
          <li>{t('privacyPolicy.s10.li1')}</li>
          <li>{t('privacyPolicy.s10.li2')}</li>
          <li>{t('privacyPolicy.s10.li3')}</li>
          <li>{t('privacyPolicy.s10.li4')}</li>
        </ul>
        <p>{t('privacyPolicy.s10.p2')}</p>
      </section>
    </>
  );
}

export function CookiesDocumentContent({
  t,
  sectionClass = 'cookies-policy-section',
  introClass = 'cookies-policy-intro',
  subblockClass = 'cookie-type-item',
}: Omit<Props, 'linkClass' | 'LinkComponent'> & { subblockClass?: string }) {
  return (
    <>
      <p className={introClass}>{t('cookiesPolicy.intro')}</p>
      <section className={sectionClass}>
        <h2>{t('cookiesPolicy.whatAre.title')}</h2>
        <p>{t('cookiesPolicy.whatAre.text')}</p>
      </section>
      <section className={sectionClass}>
        <h2>{t('cookiesPolicy.howWeUse.title')}</h2>
        <p>{t('cookiesPolicy.howWeUse.text')}</p>
        <ul>
          <li>{t('cookiesPolicy.howWeUse.item1')}</li>
          <li>{t('cookiesPolicy.howWeUse.item2')}</li>
          <li>{t('cookiesPolicy.howWeUse.item3')}</li>
        </ul>
      </section>
      <section className={sectionClass}>
        <h2>{t('cookiesPolicy.types.title')}</h2>
        <div className={subblockClass}>
          <p><strong>{t('cookiesPolicy.types.essential')}</strong></p>
          <p>{t('cookiesPolicy.types.essentialDesc')}</p>
        </div>
        <div className={subblockClass}>
          <p><strong>{t('cookiesPolicy.types.analytical')}</strong></p>
          <p>{t('cookiesPolicy.types.analyticalDesc')}</p>
        </div>
        <div className={subblockClass}>
          <p><strong>{t('cookiesPolicy.types.thirdParty')}</strong></p>
          <p>{t('cookiesPolicy.types.thirdPartyDesc')}</p>
        </div>
      </section>
      <section className={sectionClass}>
        <h2>{t('cookiesPolicy.duration.title')}</h2>
        <p>{t('cookiesPolicy.duration.text')}</p>
      </section>
      <section className={sectionClass}>
        <h2>{t('cookiesPolicy.manage.title')}</h2>
        <p>{t('cookiesPolicy.manage.text')}</p>
        <p><em>{t('cookiesPolicy.manage.withdraw')}</em></p>
      </section>
      <section className={sectionClass}>
        <h2>{t('cookiesPolicy.contact.title')}</h2>
        <p>{t('cookiesPolicy.contact.text')}</p>
      </section>
    </>
  );
}
